import { useRef, useState } from 'react';
import { useIonViewWillEnter, useIonViewDidEnter, useIonViewWillLeave } from '@ionic/react';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import type { Device } from '../../../domain/Device';
import type { Alert } from '../../../domain/Alert';
import type { Lot } from '../../../domain/Lot';

const POLL_INTERVAL_MS = 60 * 1000;
const SELECTED_KEY = 'domiot.home.selected';

// Lo último elegido en el home ("lot:<id>" o "station:<id>") es una
// comodidad de este navegador: si no se puede leer, se arranca por el primero.
function readSelected(): string | null {
  try {
    return localStorage.getItem(SELECTED_KEY);
  } catch {
    return null;
  }
}

function writeSelected(id: string) {
  try {
    localStorage.setItem(SELECTED_KEY, id);
  } catch {
    /* sin almacenamiento: no se recuerda, nada más */
  }
}

// Un pill del home: un lote (con sus estaciones adentro) o una estación que
// no está en ningún lote (o compartida, de un lote ajeno).
export type HomeItem = { kind: 'lot'; key: string; lot: Lot; devices: Device[] } | { kind: 'station'; key: string; device: Device };

export function homeItems(lots: Lot[], stations: Device[]): HomeItem[] {
  const lotIds = new Set(lots.map((l) => l.id));
  const lotItems: HomeItem[] = lots
    .map((lot) => ({ kind: 'lot' as const, key: `lot:${lot.id}`, lot, devices: stations.filter((d) => d.lotId === lot.id) }))
    // Primero el lote que tiene la estación favorita.
    .sort((a, b) => Number(b.devices.some((d) => d.isFavorite)) - Number(a.devices.some((d) => d.isFavorite)));
  const loose: HomeItem[] = stations
    .filter((d) => !d.lotId || !lotIds.has(d.lotId))
    .sort((a, b) => Number(b.isFavorite) - Number(a.isFavorite))
    .map((device) => ({ kind: 'station' as const, key: `station:${device.id}`, device }));
  return [...lotItems, ...loose];
}

// Home: un pill por lote y por estación suelta, y debajo el home del elegido.
export function useDashboard() {
  const { devices: deviceRepo, alerts: alertRepo, lots: lotRepo } = useRepositories();

  const [stations, setStations] = useState<Device[]>([]);
  const [lots, setLots] = useState<Lot[]>([]);
  const [selectedKey, setSelectedKeyState] = useState<string | null>(readSelected);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [triggeredAlerts, setTriggeredAlerts] = useState<Alert[]>([]);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = (showSpinner: boolean) => {
    if (showSpinner) setLoading(true);
    setError(null);
    Promise.all([
      deviceRepo.list(),
      lotRepo
        .list()
        .then((r) => r.lots)
        .catch((): Lot[] => []),
    ])
      .then(([{ devices: list }, lotList]) => {
        setStations(list);
        setLots(lotList);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'No se pudieron cargar las estaciones.'))
      .finally(() => setLoading(false));

    alertRepo
      .list()
      .then(({ alerts }) => setTriggeredAlerts(alerts.filter((a) => a.enabled && a.triggered)))
      .catch(() => setTriggeredAlerts([]));
  };

  useIonViewWillEnter(() => {
    load(true);
  });

  useIonViewDidEnter(() => {
    pollRef.current = setInterval(() => load(false), POLL_INTERVAL_MS);
  });

  useIonViewWillLeave(() => {
    if (pollRef.current) clearInterval(pollRef.current);
  });

  const items = homeItems(lots, stations);
  const selected = items.find((i) => i.key === selectedKey) ?? items[0] ?? null;

  const setSelectedKey = (key: string) => {
    setSelectedKeyState(key);
    writeSelected(key);
  };

  return {
    items,
    selected,
    setSelectedKey,
    loading,
    error,
    triggeredAlerts,
  };
}
